import { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Empty, List, Skeleton, Space, Typography } from "antd";
import { CheckOutlined, ReloadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import OlitaskHeaderIcon from "components/olitask/OlitaskHeaderIcon";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import paths from "routes/paths";
import {
  fetchNotificationsByApp,
  markNotificationsAsRead,
} from "services/olitaskNotificationService";
import "./OlitaskInboxPages.css";

const { Text, Title } = Typography;
const PAGE_SIZE = 20;

const categoryConfig = {
  ACTIVITY: { title: "Notifications", icon: "notification" },
  CHAT: { title: "Chats", icon: "chat" },
  EMAIL: { title: "Messages", icon: "mail" },
};

const normalizeCategory = (category) => {
  const normalized = String(category || "ACTIVITY").toUpperCase();
  return categoryConfig[normalized] ? normalized : "ACTIVITY";
};

const isUnread = (status) => ["CREATED", "DELIVERED", "NEW", "UNREAD"].includes(String(status || "").toUpperCase());
const isDeliverable = (status) => ["CREATED", "DELIVERED"].includes(String(status || "").toUpperCase());

const mergeUniqueNotifications = (existing, incoming) => {
  const map = new Map();
  existing.forEach((item) => {
    if (item?.id) map.set(String(item.id), item);
  });
  incoming.forEach((item) => {
    if (item?.id) map.set(String(item.id), item);
  });
  return Array.from(map.values()).sort((a, b) => {
    const aTime = a?.timestamp ? new Date(a.timestamp).getTime() : 0;
    const bTime = b?.timestamp ? new Date(b.timestamp).getTime() : 0;
    return bTime - aTime;
  });
};

const timeText = (value) => {
  if (!value) return "Just now";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "Just now";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export default function NotificationsPage() {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedWorkspaceId, selectedWorkspaceUse } = useWorkspace();
  const actorId = user?.id || user?.userId || "";
  const category = normalizeCategory(params.category);
  const config = categoryConfig[category];
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const unreadIds = useMemo(
    () => notifications.filter((item) => isUnread(item.status)).map((item) => item.id).filter(Boolean),
    [notifications],
  );

  const loadNotifications = useCallback(async (pageToLoad = 0, append = false) => {
    if (!actorId || !selectedWorkspaceId) {
      setNotifications([]);
      setHasMore(false);
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    setError("");
    try {
      const data = await fetchNotificationsByApp({
        userId: actorId,
        category,
        targetRole: selectedWorkspaceUse,
        page: pageToLoad,
        size: PAGE_SIZE,
        workspaceId: selectedWorkspaceId,
      });
      const fetched = Array.isArray(data) ? data : [];
      const deliverableIds = fetched.filter((item) => isDeliverable(item.status)).map((item) => item.id).filter(Boolean);
      const deliveredFetched = deliverableIds.length
        ? fetched.map((item) => (deliverableIds.includes(item.id) ? { ...item, status: "DELIVERED" } : item))
        : fetched;

      setNotifications((current) =>
        append ? mergeUniqueNotifications(current, deliveredFetched) : deliveredFetched,
      );
      setPage(pageToLoad);
      setHasMore(fetched.length >= PAGE_SIZE);

      if (deliverableIds.length) {
        await markNotificationsAsRead({
          notificationIds: deliverableIds,
          status: "DELIVERED",
          workspaceId: selectedWorkspaceId,
        });
      }
    } catch (nextError) {
      setError(nextError?.message || "Failed to load notifications.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [actorId, category, selectedWorkspaceId, selectedWorkspaceUse]);

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    setNotifications([]);
    loadNotifications(0, false);
  }, [loadNotifications]);

  const loadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    loadNotifications(page + 1, true);
  };

  const markAllRead = async () => {
    if (!unreadIds.length) return;
    await markNotificationsAsRead({
      notificationIds: unreadIds,
      status: "READ",
      workspaceId: selectedWorkspaceId,
    });
    setNotifications((current) =>
      current.map((item) => (unreadIds.includes(item.id) ? { ...item, status: "READ" } : item)),
    );
  };

  const handleNotificationPress = async (notification) => {
    const notificationWorkspaceId = notification?.workspaceId || selectedWorkspaceId;
    if (notification?.id && isUnread(notification.status)) {
      await markNotificationsAsRead({
        notificationIds: [notification.id],
        status: "READ",
        workspaceId: notificationWorkspaceId,
      });
      setNotifications((current) =>
        current.map((item) => (item.id === notification.id ? { ...item, status: "READ" } : item)),
      );
    }

    let parsedData = notification?.data;
    if (typeof parsedData === "string") {
      try {
        parsedData = JSON.parse(parsedData);
      } catch {
        parsedData = {};
      }
    }

    const normalizedCategory = normalizeCategory(notification?.category || category);
    if (normalizedCategory === "CHAT") {
      const query = new URLSearchParams();
      const chatId = parsedData?.chatId || parsedData?.taskId || parsedData?.taskRequestId;
      if (chatId) query.set("taskId", chatId);
      if (parsedData?.recipientId) query.set("recipientId", parsedData.recipientId);
      if (parsedData?.recipientName) query.set("recipientName", parsedData.recipientName);
      if (parsedData?.conversationId) query.set("conversationId", parsedData.conversationId);
      if (notificationWorkspaceId) query.set("wid", notificationWorkspaceId);
      navigate(query.toString() ? `${paths.chatSpace}?${query.toString()}` : paths.chatSpace);
      return;
    }

    if (normalizedCategory === "EMAIL") {
      navigate(paths.emailMessages);
      return;
    }

    const taskId =
      parsedData?.taskId ||
      parsedData?.taskRequestId ||
      parsedData?.task_id ||
      parsedData?.actionItemId ||
      notification?.actionItemId ||
      parsedData?.task?.id ||
      parsedData?.actionItem?.id;

    if (taskId) {
      navigate(paths.taskDetails(taskId));
    }
  };

  return (
    <div className="olitask-inbox-page">
      <div className="olitask-inbox-topbar">
        <Space align="center">
          <span className="olitask-inbox-title-icon">
            <OlitaskHeaderIcon name={config.icon} size={24} />
          </span>
          <div>
            <Title level={2} className="olitask-inbox-title">{config.title}</Title>
            <Text className="olitask-inbox-subtitle">{category} updates for the selected workspace.</Text>
          </div>
        </Space>
        <Space wrap>
          {unreadIds.length ? (
            <Button className="olitask-inbox-mark" icon={<CheckOutlined />} onClick={markAllRead}>
              Mark all as read
            </Button>
          ) : null}
          <Button className="olitask-inbox-refresh" icon={<ReloadOutlined />} onClick={() => loadNotifications(0, false)}>
            Refresh
          </Button>
        </Space>
      </div>

      {!selectedWorkspaceId ? (
        <Alert showIcon type="warning" message="Select a workspace to view notifications." />
      ) : null}
      {error ? (
        <Alert showIcon type="error" message="Unable to load notifications" description={error} />
      ) : null}

      <Card className="olitask-inbox-card" bodyStyle={{ padding: 12 }}>
        {loading ? (
          <Skeleton active paragraph={{ rows: 8 }} />
        ) : (
          <List
            dataSource={notifications}
            locale={{ emptyText: <Empty description="No notifications available." /> }}
            loadMore={
              hasMore && notifications.length ? (
                <div className="olitask-inbox-load-more">
                  <Button loading={loadingMore} onClick={loadMore}>
                    Load more notifications
                  </Button>
                </div>
              ) : null
            }
            renderItem={(item) => {
              const unread = isUnread(item.status);
              return (
                <List.Item
                  className={`olitask-inbox-row${unread ? " is-unread" : ""}`}
                  onClick={() => handleNotificationPress(item)}
                >
                  <span className="olitask-inbox-row-icon">
                    <OlitaskHeaderIcon name={config.icon} size={30} />
                  </span>
                  <span className="olitask-inbox-row-copy">
                    <Text className="olitask-inbox-row-title">{item.title || "Untitled"}</Text>
                    <Text className="olitask-inbox-row-message">
                      {item.message || "No message provided."}
                    </Text>
                    <Text className="olitask-inbox-row-time">
                      {timeText(item.timestamp || item.createdAt)}
                    </Text>
                  </span>
                </List.Item>
              );
            }}
          />
        )}
      </Card>
    </div>
  );
}
